// Shared popover positioning for slot indicators (audit B-9). Previously the
// SAME logic was copy-pasted into SlotIndicator.jsx and SlotAutoIndicator.jsx
// with DIFFERENT dimension constants (288×220 vs 256×180), so it could silently
// drift. Parameterized on the popover's own dimensions so each caller keeps its
// exact placement behavior.

export const DEFAULT_POPOVER_GAP = 8;
export const DEFAULT_POPOVER_EDGE = 8;

/**
 * Compute a viewport-clamped { left, top } for a popover anchored under a
 * trigger element. Centers horizontally on the trigger and clamps inside the
 * viewport; if the popover would overflow the viewport bottom it flips above
 * the trigger (guarded so the flipped position never goes off the top edge).
 *
 * @param {Element|null} triggerEl - anchor element (read via getBoundingClientRect)
 * @param {{ width:number, heightEstimate:number, gap?:number, edge?:number }} dims
 * @returns {{ left:number, top:number }}
 */
export function computePopoverPosition(
  triggerEl,
  { width, heightEstimate, gap = DEFAULT_POPOVER_GAP, edge = DEFAULT_POPOVER_EDGE } = {}
) {
  if (!triggerEl || typeof triggerEl.getBoundingClientRect !== "function") {
    return { left: 0, top: 0 };
  }
  const rect = triggerEl.getBoundingClientRect();
  const viewportW = typeof window !== "undefined" ? window.innerWidth : 1024;
  const viewportH = typeof window !== "undefined" ? window.innerHeight : 768;

  let left = rect.left + rect.width / 2 - width / 2;
  let top = rect.bottom + gap;

  // Clamp horizontally inside the viewport.
  left = Math.max(edge, Math.min(viewportW - width - edge, left));

  // Flip above if the popover would overflow the viewport bottom.
  if (top + heightEstimate > viewportH - edge) {
    const flipped = rect.top - heightEstimate - gap;
    if (flipped >= edge) {
      top = flipped;
    }
    top = Math.max(edge, top);
  }

  return { left, top };
}
